import { Wallet } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';

const GATEWAY_LABELS = {
  razorpay: 'Razorpay', stripe: 'Stripe', cashfree: 'Cashfree', payu: 'PayU', ccavenue: 'CCAvenue',
};

const STATUS_STYLES = {
  ok: 'bg-[#EBF7E6] text-[#2E844A] border-[#CDEFC4]',
  error: 'bg-[#FEF1EE] text-[#BA0517] border-[#FDDDE3]',
  untested: 'bg-[#F7F9FB] text-[#706E6B] border-[#E5E5E5]',
};

function GatewayCell({ config }) {
  if (!config) return <span className="text-xs text-[#C9C9C9]">—</span>;
  const status = config.isActive ? config.lastTestStatus : 'inactive';
  const style = config.isActive ? (STATUS_STYLES[config.lastTestStatus] || STATUS_STYLES.untested) : 'bg-[#F7F9FB] text-[#706E6B] border-[#E5E5E5]';
  return (
    <div
      className={`inline-flex flex-col items-start gap-0.5 px-2 py-1 rounded-lg border text-xs ${style}`}
      title={config.lastTestError || ''}
    >
      <span className="font-semibold">{config.isActive ? (config.lastTestStatus || 'untested') : 'inactive'}</span>
      {config.lastTestedAt && (
        <span className="opacity-70">{new Date(config.lastTestedAt).toLocaleDateString()}</span>
      )}
    </div>
  );
}

export default function SuperAdminPaymentGateways() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState('');

  useEffect(() => {
    api.get('/admin/payment-gateway-settings')
      .then(res => setData(res.data.data))
      .catch(() => setError('Failed to load payment gateway settings.'))
      .finally(() => setLoading(false));
  }, []);

  const loadLogs = () => {
    setLogsLoading(true);
    api.get('/admin/payment-logs', { params: { limit: 100 } })
      .then(res => setLogs(res.data.logs || []))
      .catch(() => setLogsError('Failed to load payment logs.'))
      .finally(() => setLogsLoading(false));
  };

  useEffect(() => { loadLogs(); }, []);

  if (loading) return <div className="p-8 text-sm text-[#706E6B]">Loading…</div>;
  if (error) return <div className="p-8 text-sm text-[#BA0517]">{error}</div>;

  const gateways = data.gateways || [];
  const companies = (data.companies || []).filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.companyName?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  return (
    <div className="w-full pb-10 space-y-6">
      <PageHeader title="Payment Gateway Settings" icon={Wallet} subtitle="Every organization's configured CRM invoice payment gateways, at a glance — no credential values are ever shown here." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <p className="text-xs text-[#706E6B]">Organizations with at least one gateway</p>
          <p className="text-2xl font-bold text-[#080707]">{data.totalCompaniesConfigured}</p>
        </div>
        {gateways.map(g => {
          const count = (data.companies || []).filter(c => c.gateways[g]?.isActive).length;
          return (
            <div key={g} className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <p className="text-xs text-[#706E6B]">{GATEWAY_LABELS[g] || g} — active configs</p>
              <p className="text-2xl font-bold text-[#080707]">{count}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#2E2E2E]">By Organization</h3>
          <input
            type="text"
            placeholder="Search company or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-9 px-3 border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#D8DDE6] w-64"
          />
        </div>

        {companies.length === 0 ? (
          <p className="text-sm text-[#706E6B] py-8 text-center">No organization has configured a payment gateway yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#F3F2F2]">
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Organization</th>
                  {gateways.map(g => (
                    <th key={g} className="text-left py-2 pr-4 font-medium text-[#706E6B]">{GATEWAY_LABELS[g] || g}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F2F2]">
                {companies.map(c => (
                  <tr key={c.companyId}>
                    <td className="py-2.5 pr-4">
                      <div className="font-medium text-[#2E2E2E]">{c.companyName}</div>
                      <div className="text-xs text-[#706E6B]">{c.email}</div>
                    </td>
                    {gateways.map(g => (
                      <td key={g} className="py-2.5 pr-4">
                        <GatewayCell config={c.gateways[g]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#2E2E2E]">Payment Collection Logs — All Organizations</h3>
          <button onClick={loadLogs} disabled={logsLoading} className="h-9 px-3 border border-[#E5E5E5] rounded whitespace-nowrap text-[13px] font-semibold text-[#5C5C5C] hover:bg-[#F7F9FB] disabled:opacity-50">
            {logsLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {logsError ? (
          <p className="text-sm text-[#BA0517] py-4">{logsError}</p>
        ) : logsLoading && logs.length === 0 ? (
          <p className="text-sm text-[#706E6B] py-8 text-center">Loading…</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-[#706E6B] py-8 text-center">No payment events recorded yet across any organization.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#F3F2F2]">
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Organization</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Invoice #</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Gateway</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Amount</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Status</th>
                  <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F2F2]">
                {logs.map(log => (
                  <tr key={log._id}>
                    <td className="py-2.5 pr-4">
                      <div className="font-medium text-[#2E2E2E]">{log.companyId?.companyName || 'Unknown'}</div>
                      <div className="text-xs text-[#706E6B]">{log.companyId?.email || ''}</div>
                    </td>
                    <td className="py-2.5 pr-4 text-[#444444]">{log.invoiceNumber || '—'}</td>
                    <td className="py-2.5 pr-4 text-[#444444]">{GATEWAY_LABELS[log.gateway] || log.gateway}</td>
                    <td className="py-2.5 pr-4 font-medium text-[#2E2E2E]">{log.currency} {log.amount}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${log.status === 'success' ? 'bg-[#EBF7E6] text-[#2E844A]' : 'bg-[#FEF1EE] text-[#BA0517]'}`}>
                        {log.status.toUpperCase()}
                      </span>
                      {log.failureReason && <div className="text-xs text-[#BA0517] mt-0.5">{log.failureReason}</div>}
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-[#706E6B]">{new Date(log.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
